/**
 * Drawer lateral derecho para mostrar información detallada de un producto
 */
import React, { useState, useEffect, useContext } from "react";
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Divider,
    CircularProgress,
    Stack,
    Chip,
    Alert,
    TextField,
    Button
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InventoryIcon from "@mui/icons-material/Inventory";
import QrCodeIcon from "@mui/icons-material/QrCode";
import CategoryIcon from "@mui/icons-material/Category";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import UpdateIcon from "@mui/icons-material/Update";

import ProductService from "../../../../services/product.service";
import { StateContext } from "../../../../components/context/StateProvider";
import Toasts from "../../../../components/notificacion/Toasts";
import { BADGE_COLORS, getBadgeStyles } from "../ui/badgeColors";

const DrawerProducto = ({ open, onClose, codigoProducto, getNombreAlmacen }) => {
    const { state, updateState } = useContext(StateContext);
    const [producto, setProducto] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Estado para editar la ubicación
    const [ubicacion, setUbicacion] = useState("");
    const [actualizandoUbicacion, setActualizandoUbicacion] = useState(false);

    useEffect(() => {
        if (open && codigoProducto) {
            cargarProducto();
        }
    }, [open, codigoProducto]);

    // Sincronizar ubicación cuando cambia el producto
    useEffect(() => {
        if (producto) {
            setUbicacion(producto.UBICACION || "");
        }
    }, [producto]);

    const cargarProducto = async () => {
        setLoading(true);
        setError(null);
        try {
            // Buscar el producto por código exacto
            const resultados = await ProductService.getProducts(codigoProducto);

            // Buscar coincidencia exacta por código
            const productoEncontrado = resultados?.find(p => p.CODIGO === codigoProducto);

            if (productoEncontrado) {
                setProducto(productoEncontrado);
            } else {
                setError('Producto no encontrado');
            }
        } catch (err) {
            console.error('Error al cargar producto:', err);
            setError('Error al cargar la información del producto');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setProducto(null);
        setError(null);
        setUbicacion("");
        onClose();
    };

    const handleActualizarUbicacion = async () => {
        if (!producto) {
            const toast = Toasts.generateToast('No hay producto cargado', 'error');
            updateState({ toasts: [...state.toasts, toast] });
            return;
        }

        const ubicacionOriginal = producto.UBICACION || "";
        if (ubicacion.trim() === ubicacionOriginal.trim()) {
            const toast = Toasts.generateToast('La ubicación no ha cambiado', 'warning');
            updateState({ toasts: [...state.toasts, toast] });
            return;
        }

        setActualizandoUbicacion(true);
        try {
            await ProductService.updateProduct(
                producto.CODIGO,
                { ubicacion: ubicacion.trim() },
                false
            );

            // Actualizar el producto localmente con la nueva ubicación
            const productoActualizado = { ...producto, UBICACION: ubicacion.trim() };
            setProducto(productoActualizado);

            const toast = Toasts.generateToast('Ubicación actualizada correctamente', 'success');
            updateState({ toasts: [...state.toasts, toast] });
        } catch (error) {
            console.error('Error al actualizar ubicación:', error);
            const toast = Toasts.generateToast(
                error.response?.data?.message || 'Error al actualizar ubicación',
                'error'
            );
            updateState({ toasts: [...state.toasts, toast] });
        } finally {
            setActualizandoUbicacion(false);
        }
    };

    return (
        <Drawer anchor="right" open={open} onClose={handleClose}>
            <Box sx={{ width: { xs: "100vw", md: 520 }, p: { xs: 2, sm: 3 } }}>
                {/* Header */}
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 2 }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                backgroundColor: "#fee",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                            }}
                        >
                            <InventoryIcon sx={{ color: "#cc071e", fontSize: 22 }} />
                        </Box>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                color: "#212529",
                                fontSize: { xs: "1.1rem", sm: "1.25rem" },
                            }}
                        >
                            Información del Producto
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={handleClose}
                        size="small"
                        sx={{
                            color: "#6c757d",
                            "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.04)",
                            },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 24 }} />
                    </IconButton>
                </Stack>

                <Divider sx={{ mb: 3 }} />

                {/* Contenido */}
                {loading ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: "300px",
                        }}
                    >
                        <Box sx={{ textAlign: "center" }}>
                            <CircularProgress sx={{ color: "#cc071e", mb: 2 }} />
                            <Typography sx={{ color: "#6c757d", fontSize: 14 }}>
                                Cargando producto...
                            </Typography>
                        </Box>
                    </Box>
                ) : error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : !producto ? (
                    <Typography variant="body2" sx={{ color: "#6c757d", textAlign: "center", py: 6 }}>
                        Sin datos.
                    </Typography>
                ) : (
                    <Stack spacing={3}>
                        {/* Código y Nombre */}
                        <Box>
                            <Typography
                                variant="overline"
                                sx={{
                                    color: "#6c757d",
                                    fontWeight: 600,
                                    fontSize: 11,
                                    letterSpacing: 1,
                                }}
                            >
                                Código
                            </Typography>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 700,
                                    color: "#212529",
                                    mb: 1,
                                }}
                            >
                                {producto.CODIGO}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: "#495057",
                                    fontWeight: 500,
                                    lineHeight: 1.5,
                                }}
                            >
                                {producto.NOMBRE}
                            </Typography>
                        </Box>

                        <Divider />

                        {/* Ubicación editable */}
                        <Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    mb: 1.5,
                                }}
                            >
                                <LocationOnIcon
                                    sx={{ fontSize: 20, color: "#6c757d" }}
                                />
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontWeight: 600,
                                        color: "#495057",
                                    }}
                                >
                                    Ubicación
                                </Typography>
                            </Box>
                            <Stack spacing={1.5}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    placeholder="Ubicación del producto..."
                                    value={ubicacion}
                                    onChange={(e) => setUbicacion(e.target.value)}
                                    disabled={actualizandoUbicacion}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            backgroundColor: "#fff",
                                        },
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    startIcon={actualizandoUbicacion ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <UpdateIcon />}
                                    onClick={handleActualizarUbicacion}
                                    disabled={
                                        actualizandoUbicacion ||
                                        ubicacion.trim() === (producto.UBICACION || "").trim()
                                    }
                                    fullWidth
                                    sx={{
                                        backgroundColor: "#cc071e",
                                        textTransform: "none",
                                        fontWeight: 600,
                                        py: 1,
                                        fontSize: 14,
                                        "&:hover": {
                                            backgroundColor: "#a30618",
                                        },
                                        "&:disabled": {
                                            backgroundColor: "#6c757d",
                                            color: "#fff",
                                        },
                                    }}
                                >
                                    {actualizandoUbicacion ? 'Actualizando...' : 'Actualizar Ubicación'}
                                </Button>
                            </Stack>
                        </Box>

                        {/* Códigos de Barras */}
                        {producto.BarrasModels && producto.BarrasModels.length > 0 && (
                            <Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mb: 1.5,
                                    }}
                                >
                                    <QrCodeIcon
                                        sx={{ fontSize: 20, color: "#6c757d" }}
                                    />
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            color: "#495057",
                                        }}
                                    >
                                        Códigos de Barras
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                    {producto.BarrasModels.map((barraModel, idx) => (
                                        <Chip
                                            key={idx}
                                            label={barraModel.BARRAS}
                                            size="small"
                                            sx={getBadgeStyles(BADGE_COLORS.RED, { fontSize: 12 })}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Familia */}
                        {producto.FAMILIA && (
                            <Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mb: 1,
                                    }}
                                >
                                    <CategoryIcon
                                        sx={{ fontSize: 20, color: "#6c757d" }}
                                    />
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            color: "#495057",
                                        }}
                                    >
                                        Familia
                                    </Typography>
                                </Box>
                                <Typography
                                    sx={{
                                        pl: 4,
                                        fontSize: 14,
                                        color: "#495057",
                                    }}
                                >
                                    {producto.FAMILIA}
                                </Typography>
                            </Box>
                        )}

                        {/* Subfamilia */}
                        {producto.SUBFAMILIA && (
                            <Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mb: 1,
                                    }}
                                >
                                    <LocalOfferIcon
                                        sx={{ fontSize: 20, color: "#6c757d" }}
                                    />
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            color: "#495057",
                                        }}
                                    >
                                        Subfamilia
                                    </Typography>
                                </Box>
                                <Typography
                                    sx={{
                                        pl: 4,
                                        fontSize: 14,
                                        color: "#495057",
                                    }}
                                >
                                    {producto.SUBFAMILIA}
                                </Typography>
                            </Box>
                        )}

                        {/* Precio */}
                        {producto.PRECIO !== undefined && producto.PRECIO !== null && (
                            <Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mb: 1,
                                    }}
                                >
                                    <AttachMoneyIcon
                                        sx={{ fontSize: 20, color: "#6c757d" }}
                                    />
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            color: "#495057",
                                        }}
                                    >
                                        Precio
                                    </Typography>
                                </Box>
                                <Typography
                                    sx={{
                                        pl: 4,
                                        fontSize: 18,
                                        color: BADGE_COLORS.GREEN.text,
                                        fontWeight: 700,
                                    }}
                                >
                                    {Number(producto.PRECIO).toFixed(2)} €
                                </Typography>
                            </Box>
                        )}

                        {/* Stock por almacén */}
                        {producto.StockModels && producto.StockModels.length > 0 && (
                            <Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mb: 1.5,
                                    }}
                                >
                                    <WarehouseIcon
                                        sx={{ fontSize: 20, color: "#6c757d" }}
                                    />
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            color: "#495057",
                                        }}
                                    >
                                        Stock por Almacén
                                    </Typography>
                                </Box>
                                <Stack spacing={1} sx={{ pl: 4 }}>
                                    {producto.StockModels.map((stock, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                p: 1.5,
                                                backgroundColor: "#f8f9fa",
                                                borderRadius: 1,
                                                border: "1px solid #dee2e6",
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: 13,
                                                    fontWeight: 600,
                                                    color: "#495057",
                                                }}
                                            >
                                                {getNombreAlmacen ? getNombreAlmacen(stock.ALMACEN) : stock.ALMACEN}
                                            </Typography>
                                            <Chip
                                                label={`${stock.CANTIDAD} uds`}
                                                size="small"
                                                sx={getBadgeStyles(
                                                    stock.CANTIDAD > 0 ? BADGE_COLORS.GREEN : BADGE_COLORS.RED,
                                                    { fontSize: 12 }
                                                )}
                                            />
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </Stack>
                )}
            </Box>
        </Drawer>
    );
};

export default DrawerProducto;
